import React, { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useI18n } from "../i18n";
import { LOGO_URL } from "../data/properties";

export const Header = () => {
  const { lang, setLang, t } = useI18n();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  const navItems = [
    { to: "/", label: t.nav.home },
    { to: "/services", label: t.nav.services },
    { to: "/logements", label: t.nav.properties },
    { to: "/vallee", label: t.nav.valley },
    { to: "/contact", label: t.nav.contact },
  ];

  React.useEffect(() => { setOpen(false); }, [loc.pathname]);

  return (
    <header className="sticky top-0 z-50 backdrop-blur-md bg-[hsl(var(--bg))]/85 border-b border-[hsl(var(--line))]" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
          <img src={LOGO_URL} alt="MyHost" width="48" height="48" className="h-12 w-12 rounded-full object-cover ring-1 ring-[hsl(var(--beige-deep))]/40" />
          <div className="hidden sm:block leading-tight">
            <div className="font-serif text-xl tracking-wide">MyHost</div>
            <div className="text-[10px] uppercase tracking-[.2em] text-[hsl(var(--muted-ink))]">Chamonix</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${n.to.replace('/', '') || 'home'}`}
              className={({ isActive }) =>
                `text-sm tracking-wide transition-colors ${isActive ? 'text-[hsl(var(--gold-deep))] font-semibold' : 'text-[hsl(var(--ink))] hover:text-[hsl(var(--gold-deep))]'}`
              }>
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')} data-testid="lang-toggle"
            className="hidden sm:flex items-center gap-2 text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-full border border-[hsl(var(--line))] hover:border-[hsl(var(--gold))] transition-colors">
            <span className="text-base leading-none" aria-hidden="true">{lang === 'fr' ? '🇫🇷' : '🇬🇧'}</span> {lang.toUpperCase()}
          </button>
          <Link to="/louer-votre-bien" data-testid="header-rent-btn"
            className="hidden md:inline-block btn-gold px-5 py-2.5 rounded-full text-sm font-semibold">
            {t.nav.rent}
          </Link>
          <button className="lg:hidden p-2" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="menu">
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-[hsl(var(--line))] bg-[hsl(var(--bg))] px-5 py-6 space-y-4" data-testid="mobile-menu">
          {navItems.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} className="block text-base font-medium">
              {n.label}
            </NavLink>
          ))}
          <Link to="/louer-votre-bien" className="block btn-gold px-5 py-3 rounded-full text-sm text-center font-semibold">{t.nav.rent}</Link>
          <button onClick={() => setLang(lang === 'fr' ? 'en' : 'fr')} className="w-full flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider px-3 py-2 rounded-full border">
            <span className="text-base leading-none" aria-hidden="true">{lang === 'fr' ? '🇬🇧' : '🇫🇷'}</span> {lang === 'fr' ? 'English' : 'Français'}
          </button>
        </div>
      )}
    </header>
  );
};

export const Footer = () => {
  const { t } = useI18n();
  return (
    <footer className="bg-[hsl(var(--ink))] text-[hsl(var(--bg))] mt-24" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-16 grid md:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3 mb-4">
            <img src={LOGO_URL} alt="MyHost" width="56" height="56" className="h-14 w-14 rounded-full" />
            <div>
              <div className="font-serif text-2xl">MyHost</div>
              <div className="text-[10px] uppercase tracking-[.2em] opacity-70">Location Saisonnière</div>
            </div>
          </div>
          <p className="text-sm opacity-80 leading-relaxed max-w-xs">{t.footer.tagline}</p>
        </div>
        <div>
          <h4 className="font-serif text-lg mb-4">{t.footer.nav}</h4>
          <ul className="space-y-2 text-sm opacity-80">
            <li><Link to="/">{t.nav.home}</Link></li>
            <li><Link to="/services">{t.nav.services}</Link></li>
            <li><Link to="/logements">{t.nav.properties}</Link></li>
            <li><Link to="/vallee">{t.nav.valley}</Link></li>
            <li><Link to="/contact">{t.nav.contact}</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-serif text-lg mb-4">{t.footer.contactT}</h4>
          <ul className="space-y-2 text-sm opacity-80">
            <li><a href="tel:+33614488635">+33 6 14 48 86 35</a></li>
            <li><a href="mailto:myhost.claudiu@gmail.com">myhost.claudiu@gmail.com</a></li>
            <li>Vallée de Chamonix Mont-Blanc</li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs opacity-60">
        © {new Date().getFullYear()} MyHost — {t.footer.rights}
      </div>
    </footer>
  );
};
